import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { vehicleName } from '../lib/utils';
import { useMonthOptions } from '../lib/useMonthOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, ArrowLeft, Calendar, Car, AlertTriangle, CheckCircle2, Clock, ChevronDown, ChevronUp, Plus, Trash2, PenLine, Check, Receipt, Ban } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import ModuleBg from '../components/ModuleBg';

function fmt(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v); }
function fmt2(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v); }
function mc(m) { return m >= 1000 ? 'text-emerald-600' : m >= 500 ? 'text-amber-600' : 'text-red-600'; }
function initials(name) { return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); }
const ACTION_LABELS = { create: 'Création', update: 'Modification', delete: 'Suppression' };
const ENTITY_LABELS = { vehicle: 'Véhicule', sav: 'SAV', frais_bureau: 'Frais bureau', charge: 'Charge', ecriture_commerciale: 'Écriture', user: 'Utilisateur' };
const TYPE_AVANCE_LABELS = { acompte: 'Acompte', frais_divers: 'Frais divers', achat_vehicule: 'Achat véhicule', achat_pieces: 'Achat pièces', autre: 'Autre' };
const TYPE_AVANCE_COLORS = { acompte: 'bg-blue-50 text-blue-700', frais_divers: 'bg-amber-50 text-amber-700', achat_vehicule: 'bg-purple-50 text-purple-700', achat_pieces: 'bg-orange-50 text-orange-700', autre: 'bg-gray-100 text-gray-600' };
const ETAT_LABELS = { a_deduire: 'À déduire', a_recuperer: 'À déduire', partiellement_deduit: 'Partiel', solde: 'Soldé', soldee: 'Soldé', annule: 'Annulé' };
const ETAT_COLORS = { a_deduire: 'bg-amber-50 text-amber-700 border-amber-200', a_recuperer: 'bg-amber-50 text-amber-700 border-amber-200', partiellement_deduit: 'bg-blue-50 text-blue-700 border-blue-200', solde: 'bg-emerald-50 text-emerald-700 border-emerald-200', soldee: 'bg-emerald-50 text-emerald-700 border-emerald-200', annule: 'bg-gray-50 text-gray-400 border-gray-200' };

export default function CompteCommercialPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = ['super_admin', 'admin', 'admin_agence'].includes(user?.role);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mois, setMois] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
    const [showVentes, setShowVentes] = useState(false);
    const [tab, setTab] = useState(searchParams.get('tab') || 'resume');
    const monthOptions = useMonthOptions();

    // Écriture dialog
    const [showEcritureDialog, setShowEcritureDialog] = useState(false);
    const [editingEcriture, setEditingEcriture] = useState(null);
    const [ecForm, setEcForm] = useState({ libelle: '', montant: '', type: 'deduction', type_avance: 'acompte', commentaire: '', date: '', mois_concerne: '' });

    // Solder dialog
    const [showSolderDialog, setShowSolderDialog] = useState(false);
    const [deductionAmounts, setDeductionAmounts] = useState({});

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/compte-commercial/${id}?mois=${mois}`, { withCredentials: true });
            setData(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [id, mois]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Écriture CRUD
    const openNewEcriture = () => {
        setEditingEcriture(null);
        setEcForm({ libelle: '', montant: '', type: 'deduction', type_avance: 'acompte', commentaire: '', date: new Date().toISOString().slice(0, 10), mois_concerne: mois });
        setShowEcritureDialog(true);
    };
    const openEditEcriture = (ec) => {
        setEditingEcriture(ec);
        setEcForm({ libelle: ec.libelle, montant: ec.montant_initial || ec.montant, type: ec.type, type_avance: ec.type_avance || 'autre', commentaire: ec.commentaire || '', date: ec.date || '', mois_concerne: ec.mois_concerne || '' });
        setShowEcritureDialog(true);
    };
    const saveEcriture = async () => {
        if (!ecForm.libelle.trim() || !ecForm.montant) { toast.error('Libellé et montant requis'); return; }
        try {
            if (editingEcriture) {
                await axios.put(`${API_URL}/api/ecritures-commerciales/${editingEcriture.id}`, { ...ecForm, montant: parseFloat(ecForm.montant) }, { withCredentials: true });
                toast.success('Écriture modifiée');
            } else {
                await axios.post(`${API_URL}/api/ecritures-commerciales`, { ...ecForm, commercial_id: id, montant: parseFloat(ecForm.montant) }, { withCredentials: true });
                toast.success('Écriture créée');
            }
            setShowEcritureDialog(false);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    const deleteEcriture = async (ecId) => {
        if (!window.confirm('Supprimer cette écriture ?')) return;
        try {
            await axios.delete(`${API_URL}/api/ecritures-commerciales/${ecId}`, { withCredentials: true });
            toast.success('Écriture supprimée');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };
    const cancelEcriture = async (ecId) => {
        try {
            await axios.put(`${API_URL}/api/ecritures-commerciales/${ecId}`, { etat: 'annule' }, { withCredentials: true });
            toast.success('Écriture annulée');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    // Solder
    const openSolder = () => {
        const pending = (data?.ecritures || []).filter(e => !['solde', 'soldee', 'annule'].includes(e.etat));
        const initial = {};
        pending.forEach(e => { initial[e.id] = e.solde_restant || e.montant; });
        setDeductionAmounts(initial);
        setShowSolderDialog(true);
    };
    const solderEcritures = async () => {
        const deductions = Object.entries(deductionAmounts)
            .filter(([_, m]) => m > 0)
            .map(([id, montant]) => ({ id, montant: parseFloat(montant) }));
        if (deductions.length === 0) { toast.error('Aucune déduction sélectionnée'); return; }
        try {
            await axios.post(`${API_URL}/api/ecritures-commerciales/solder`, { deductions, mois_commission: mois }, { withCredentials: true });
            toast.success(`${deductions.length} écriture(s) traitée(s)`);
            setShowSolderDialog(false);
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
    if (!data) return <div className="text-center text-gray-400 py-20">Compte non trouvé</div>;

    const progressColor = data.objectif_pct >= 80 ? 'bg-emerald-500' : data.objectif_pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
    const ecrituresPending = (data.ecritures || []).filter(e => !['solde', 'soldee', 'annule'].includes(e.etat));
    const ecrituresSoldees = (data.ecritures || []).filter(e => ['solde', 'soldee'].includes(e.etat));
    const ecrituresAnnulees = (data.ecritures || []).filter(e => e.etat === 'annule');

    // Solder calculation
    const totalDeductions = Object.entries(deductionAmounts).reduce((s, [id, m]) => {
        const ec = ecrituresPending.find(e => e.id === id);
        return s + (ec?.type === 'deduction' ? parseFloat(m) || 0 : 0);
    }, 0);
    const totalCredits = Object.entries(deductionAmounts).reduce((s, [id, m]) => {
        const ec = ecrituresPending.find(e => e.id === id);
        return s + (ec?.type === 'credit' ? parseFloat(m) || 0 : 0);
    }, 0);
    const netAVerser = data.commission_brute - totalDeductions + totalCredits;

    const TABS = [
        { key: 'resume', label: 'Résumé' },
        { key: 'avances', label: `Avances et frais (${ecrituresPending.length})` },
        { key: 'historique', label: 'Historique' },
    ];

    return (
        <ModuleBg>
        <div className="max-w-[900px] mx-auto px-6 py-10" data-testid="compte-commercial-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate('/commissions')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold bg-indigo-100 text-indigo-700">{initials(data.commercial_name)}</div>
                <div className="flex-1">
                    <h1 className="text-[17px] font-semibold text-gray-900">{data.commercial_name}</h1>
                    <p className="text-[13px] text-gray-400">{data.nb_ventes} vente{data.nb_ventes > 1 ? 's' : ''} ce mois</p>
                </div>
                <Select value={mois} onValueChange={setMois}>
                    <SelectTrigger className="w-48 bg-white border-gray-200 text-[13px] h-9 rounded-lg shadow-sm"><Calendar className="w-3.5 h-3.5 mr-2 text-gray-400" /><SelectValue /></SelectTrigger>
                    <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="card p-5"><p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2 font-medium">Marge brute</p><p className={`text-2xl font-bold font-mono ${mc(data.marge_vehicules)}`}>{fmt(data.marge_vehicules)}</p></div>
                <div className="card p-5"><p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2 font-medium">Commission</p><p className="text-2xl font-bold font-mono text-emerald-600">{fmt(data.commission_brute)}</p><p className="text-[11px] text-gray-400 mt-1">Taux : {data.taux}%</p></div>
                <div className="card p-5"><p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2 font-medium">Avances à déduire</p><p className={`text-2xl font-bold font-mono ${(data.ecritures_deductions_pending || 0) > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{fmt(data.ecritures_deductions_pending || 0)}</p></div>
                <div className="card p-5"><p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2 font-medium">Objectif</p><p className={`text-2xl font-bold font-mono ${data.objectif_pct >= 80 ? 'text-emerald-600' : data.objectif_pct >= 50 ? 'text-amber-600' : 'text-gray-700'}`}>{data.objectif > 0 ? `${data.objectif_pct.toFixed(0)} %` : '—'}</p>
                    {data.objectif > 0 && <div className="w-full h-[5px] rounded-full bg-gray-100 overflow-hidden mt-2"><div className={`h-full rounded-full transition-all duration-700 ${progressColor}`} style={{ width: `${Math.min(100, data.objectif_pct)}%` }} /></div>}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl" data-testid="compte-tabs">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
                ))}
            </div>

            {/* === TAB: Résumé === */}
            {tab === 'resume' && (<>
                <div className="card p-6 mb-6">
                    <h2 className="text-[13px] font-semibold text-gray-900 mb-4">Détail commission</h2>
                    <div className="space-y-2.5 text-[13px]">
                        <Row label="Marge brute véhicules" value={fmt2(data.marge_vehicules)} bold />
                        <div className="border-t border-gray-100 my-1" />
                        <Row label="SAV (retenues du mois)" value={data.sav_month > 0 ? `− ${fmt2(data.sav_month)}` : '—'} color={data.sav_month > 0 ? 'text-red-500' : 'text-gray-300'} />
                        <Row label="Frais de bureau (du mois)" value={data.fb_month > 0 ? `− ${fmt2(data.fb_month)}` : '—'} color={data.fb_month > 0 ? 'text-red-500' : 'text-gray-300'} />
                        <div className="border-t border-gray-100 my-1" />
                        <Row label="Marge nette" value={fmt2(data.marge_nette)} bold color={data.marge_nette >= 0 ? 'text-gray-900' : 'text-red-600'} />
                        <Row label={`Seuil : ${fmt(data.seuil)} — Taux : ${data.taux}%`} value="" color="text-gray-400" />
                        <div className="border-t border-gray-100 my-1" />
                        <div className="flex justify-between pt-1"><span className="text-gray-900 font-semibold">Commission brute</span><span className="text-xl font-bold font-mono text-emerald-600">{fmt2(data.commission_brute)}</span></div>
                    </div>
                </div>
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-[13px] font-semibold text-gray-900">Ventes du mois</h2>
                        {data.ventes.length > 3 && <button onClick={() => setShowVentes(!showVentes)} className="text-[12px] text-[#6366F1] hover:text-[#4F46E5] font-medium flex items-center gap-1">{showVentes ? 'Réduire' : `Voir tout (${data.ventes.length})`}{showVentes ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</button>}
                    </div>
                    {data.ventes.length === 0 && <p className="text-[13px] text-gray-400 py-4 text-center">Aucune vente ce mois</p>}
                    <div className="space-y-0.5">
                        {(showVentes ? data.ventes : data.ventes.slice(0, 3)).map(v => (
                            <div key={v.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/calculator/${v.id}`)}>
                                <div className="flex items-center gap-2.5"><Car className="w-4 h-4 text-gray-300" strokeWidth={1.5} /><div><p className="text-[13px] text-gray-700 font-medium truncate max-w-[200px]" title={vehicleName(v)}>{vehicleName(v)}</p>{v.immatriculation && <p className="text-[11px] text-gray-400 font-mono">{v.immatriculation}</p>}</div></div>
                                <div className="text-right"><p className={`text-[13px] font-mono font-semibold ${mc(v.marge_ht)}`}>{fmt(v.marge_ht)}</p><p className="text-[11px] text-gray-400 font-mono">{v.date_vente}</p></div>
                            </div>
                        ))}
                    </div>
                </div>
            </>)}

            {/* === TAB: Avances et frais === */}
            {tab === 'avances' && (
                <div className="space-y-4">
                    {/* Header + Actions */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-[15px] font-semibold text-gray-900">Avances et frais divers</h2>
                        <div className="flex gap-2">
                            {isAdmin && ecrituresPending.length > 0 && <Button variant="outline" size="sm" onClick={openSolder} className="text-[12px] h-8 rounded-lg" data-testid="solder-btn"><Check className="w-3.5 h-3.5 mr-1" />Déduire</Button>}
                            {isAdmin && <Button size="sm" onClick={openNewEcriture} className="text-[12px] h-8 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="new-ecriture-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter une avance ou un frais</Button>}
                        </div>
                    </div>

                    {/* Pending */}
                    {ecrituresPending.length > 0 && (
                        <div className="card p-5" data-testid="ecritures-pending">
                            <p className="text-[11px] text-amber-600 font-semibold uppercase tracking-widest mb-3">À déduire · {fmt(data.ecritures_deductions_pending || 0)}</p>
                            <div className="space-y-2">
                                {ecrituresPending.map(e => (
                                    <div key={e.id} className="flex items-start justify-between p-3 rounded-xl bg-gray-50 group" data-testid={`ecriture-${e.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${TYPE_AVANCE_COLORS[e.type_avance] || TYPE_AVANCE_COLORS.autre}`}>{TYPE_AVANCE_LABELS[e.type_avance] || 'Autre'}</span>
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${ETAT_COLORS[e.etat] || ETAT_COLORS.a_deduire}`}>{ETAT_LABELS[e.etat] || e.etat}</span>
                                                {e.mois_concerne && <span className="text-[10px] text-gray-400">{e.mois_concerne}</span>}
                                            </div>
                                            <p className="text-[13px] text-gray-800 font-medium mt-1">{e.libelle}</p>
                                            {e.commentaire && <p className="text-[11px] text-gray-400 mt-0.5">{e.commentaire}</p>}
                                            <p className="text-[10px] text-gray-300 mt-0.5">{e.date} · {e.created_by_name || ''}</p>
                                            {e.montant_deduit > 0 && (
                                                <div className="mt-1 text-[11px] text-blue-600">
                                                    Déjà déduit : {fmt(e.montant_deduit)} — Solde : {fmt(e.solde_restant)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-3">
                                            <div className="text-right">
                                                <p className={`text-[14px] font-mono font-semibold ${e.type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>{e.type === 'credit' ? '+' : ''}{fmt(e.solde_restant || e.montant)}</p>
                                                {e.montant_deduit > 0 && <p className="text-[10px] text-gray-400 font-mono line-through">{fmt(e.montant_initial)}</p>}
                                            </div>
                                            {isAdmin && (
                                                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => openEditEcriture(e)} className="p-1 hover:bg-white rounded" title="Modifier"><PenLine className="w-3 h-3 text-gray-400" /></button>
                                                    <button onClick={() => cancelEcriture(e.id)} className="p-1 hover:bg-red-50 rounded" title="Annuler"><Ban className="w-3 h-3 text-red-400" /></button>
                                                    <button onClick={() => deleteEcriture(e.id)} className="p-1 hover:bg-red-50 rounded" title="Supprimer"><Trash2 className="w-3 h-3 text-red-400" /></button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Soldées */}
                    {ecrituresSoldees.length > 0 && (
                        <div className="card p-5">
                            <p className="text-[11px] text-emerald-600 font-semibold uppercase tracking-widest mb-3">Soldées</p>
                            <div className="space-y-1.5">
                                {ecrituresSoldees.map(e => (
                                    <div key={e.id} className="flex items-center justify-between p-2.5 rounded-lg opacity-60">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13px] text-gray-500">{e.libelle}</p>
                                            <p className="text-[10px] text-gray-300">{e.solde_date?.slice(0, 10)} · par {e.solde_par} · comm. {e.solde_commission_mois}</p>
                                        </div>
                                        <span className="text-[13px] font-mono text-gray-400">{fmt(e.montant_initial || e.montant)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {ecrituresPending.length === 0 && ecrituresSoldees.length === 0 && ecrituresAnnulees.length === 0 && (
                        <div className="card p-8 text-center">
                            <Receipt className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                            <p className="text-sm text-gray-400">Aucune avance ou frais divers</p>
                            {isAdmin && <p className="text-[12px] text-gray-300 mt-1">Cliquez "Ajouter" pour créer une écriture</p>}
                        </div>
                    )}
                </div>
            )}

            {/* === TAB: Historique === */}
            {tab === 'historique' && (
                <div className="card p-6">
                    <h2 className="text-[13px] font-semibold text-gray-900 mb-4">Historique des modifications</h2>
                    {data.historique.length === 0 && <p className="text-[13px] text-gray-400 py-4 text-center">Aucun historique</p>}
                    <div className="space-y-2">
                        {data.historique.map((a, i) => (
                            <div key={i} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-gray-50">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${a.action === 'create' ? 'bg-emerald-50 text-emerald-600' : a.action === 'delete' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                                    {a.action === 'create' ? <CheckCircle2 className="w-3.5 h-3.5" /> : a.action === 'delete' ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] text-gray-700">{a.comment || `${ACTION_LABELS[a.action] || a.action} ${ENTITY_LABELS[a.entity_type] || a.entity_type}`}</p>
                                    <p className="text-[10px] text-gray-300 mt-1">{a.user_name} · {new Date(a.timestamp).toLocaleString('fr-FR')}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* === Écriture Dialog === */}
            <Dialog open={showEcritureDialog} onOpenChange={setShowEcritureDialog}>
                <DialogContent className="bg-white text-gray-900 max-w-md">
                    <DialogHeader><DialogTitle>{editingEcriture ? 'Modifier' : 'Nouvelle avance ou frais'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Type</Label>
                            <Select value={ecForm.type_avance} onValueChange={v => setEcForm({...ecForm, type_avance: v})}>
                                <SelectTrigger data-testid="ec-type-avance"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="acompte">Acompte</SelectItem>
                                    <SelectItem value="frais_divers">Frais divers</SelectItem>
                                    <SelectItem value="achat_vehicule">Achat véhicule</SelectItem>
                                    <SelectItem value="achat_pieces">Achat pièces</SelectItem>
                                    <SelectItem value="autre">Autre</SelectItem>
                                </SelectContent>
                            </Select></div>
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Libellé</Label>
                            <Input value={ecForm.libelle} onChange={e => setEcForm({...ecForm, libelle: e.target.value})} placeholder="Ex: Acompte février, 4 pneus RS3..." className="bg-white" data-testid="ec-libelle" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Montant (€)</Label>
                                <Input type="number" inputMode="decimal" value={ecForm.montant} onChange={e => setEcForm({...ecForm, montant: e.target.value})} placeholder="0" className="bg-white" data-testid="ec-montant" /></div>
                            <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Déduction / Crédit</Label>
                                <Select value={ecForm.type} onValueChange={v => setEcForm({...ecForm, type: v})}>
                                    <SelectTrigger data-testid="ec-type"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="deduction">Déduction</SelectItem><SelectItem value="credit">Crédit</SelectItem></SelectContent>
                                </Select></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Date</Label>
                                <Input type="date" value={ecForm.date} onChange={e => setEcForm({...ecForm, date: e.target.value})} className="bg-white" data-testid="ec-date" /></div>
                            <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Mois concerné</Label>
                                <Select value={ecForm.mois_concerne} onValueChange={v => setEcForm({...ecForm, mois_concerne: v})}>
                                    <SelectTrigger data-testid="ec-mois"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                </Select></div>
                        </div>
                        <div className="space-y-1.5"><Label className="text-gray-500 text-[11px] uppercase tracking-wider">Commentaire</Label>
                            <Input value={ecForm.commentaire} onChange={e => setEcForm({...ecForm, commentaire: e.target.value})} placeholder="Optionnel" className="bg-white" data-testid="ec-commentaire" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEcritureDialog(false)}>Annuler</Button>
                        <Button onClick={saveEcriture} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="ec-save-btn">Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* === Solder Dialog (with partial deduction) === */}
            <Dialog open={showSolderDialog} onOpenChange={setShowSolderDialog}>
                <DialogContent className="bg-white text-gray-900 max-w-lg">
                    <DialogHeader><DialogTitle>Déduire des avances et frais</DialogTitle></DialogHeader>
                    <p className="text-[13px] text-gray-500 mb-4">Ajustez les montants à déduire de la commission <strong>{mois}</strong>.</p>
                    <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
                        {ecrituresPending.map(e => (
                            <div key={e.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50" data-testid={`solder-row-${e.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] text-gray-700 font-medium">{e.libelle}</p>
                                    <p className="text-[10px] text-gray-400">{TYPE_AVANCE_LABELS[e.type_avance] || 'Autre'} · Solde : {fmt(e.solde_restant || e.montant)}</p>
                                </div>
                                <div className="w-28 shrink-0">
                                    <Input type="number" inputMode="decimal" min="0" max={e.solde_restant || e.montant} step="0.01"
                                        value={deductionAmounts[e.id] ?? ''}
                                        onChange={ev => setDeductionAmounts(prev => ({...prev, [e.id]: ev.target.value}))}
                                        className="bg-white text-right text-[13px] h-9 font-mono"
                                        data-testid={`solder-amount-${e.id}`} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-[13px]">
                        <div className="flex justify-between"><span className="text-gray-500">Commission brute</span><span className="font-mono font-medium text-emerald-600">{fmt2(data.commission_brute)}</span></div>
                        {totalDeductions > 0 && <div className="flex justify-between"><span className="text-gray-500">Déductions</span><span className="font-mono text-red-500">− {fmt2(totalDeductions)}</span></div>}
                        {totalCredits > 0 && <div className="flex justify-between"><span className="text-gray-500">Crédits</span><span className="font-mono text-emerald-600">+ {fmt2(totalCredits)}</span></div>}
                        <div className="border-t border-gray-200 pt-2 flex justify-between">
                            <span className="text-gray-900 font-semibold">Montant à verser</span>
                            <span className={`text-lg font-bold font-mono ${netAVerser >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt2(netAVerser)}</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSolderDialog(false)}>Annuler</Button>
                        <Button onClick={solderEcritures} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="solder-confirm-btn">Valider les déductions</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </ModuleBg>
    );
}

function Row({ label, value, color = 'text-gray-700', bold }) {
    return (
        <div className="flex justify-between py-0.5">
            <span className={bold ? 'text-gray-700 font-medium' : 'text-gray-500'}>{label}</span>
            {value && <span className={`font-mono ${bold ? 'font-medium' : ''} ${color}`}>{value}</span>}
        </div>
    );
}
